<template>

  <unauthed-panel :title="$t('titles.confirm_your_account')">

	  <v-form @submit.prevent="submit" novalidate>

	    <o-text-field
	      :label="$t('titles.username_or_email_address')"
	      v-model.trim="v$.identifier.$model"
	      :validator="v$.identifier"
        prepend-inner-icon="$user"
        required
        autofocus
        autocomplete="username"
	    />

	    <o-text-field
	      :label="$t('titles.temporary_password')"
	      type="password"
	      v-model.trim="v$.tempPassword.$model"
	      :validator="v$.tempPassword"
        prepend-inner-icon="$password"
        required
        autocomplete="current-password"
	    />

	    <o-text-field
	      :label="$t('titles.new_password')"
	      type="password"
	      :hint="$t('misc.password_requirements')"
        persistent-hint
	      v-model.trim="v$.newPassword1.$model"
	      :validator="v$.newPassword1"
        prepend-inner-icon="$password"
        required
        autocomplete="new-password"
	    />

	    <o-text-field
	      :label="$t('titles.repeat_new_password')"
	      type="password"
	      v-model.trim="v$.newPassword2.$model"
	      :validator="v$.newPassword2"
        prepend-inner-icon="$password"
        required
        autocomplete="new-password"
	    />

	    <v-btn
	      type="submit"
        block
        prepend-icon="$confirm"
	      :loading="submitting"
	      :disabled="submitting"
        class="mt-8"
	    >{{ $t('actions.confirm_account') }}</v-btn>

	  </v-form>
  
  </unauthed-panel>

</template>

<script setup lang="ts">

import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useTitle } from '@vueuse/core'
import store from '../../plugins/vuex'
import UnauthedPanel from '../UnauthedPanel.vue'
import NotificationService from '../../services/NotificationService'

// validation
import useVuelidate from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'
import { required } from '../../utils/i18n-validators'

import passwordComplexity from '../../rules/passwordComplexity'

const router = useRouter()
const { t } = useI18n()

const title = useTitle()
title.value = t('titles.ocra_confirm_your_account')

// if the user is already logged in, then redirect
if (store.state.auth.status.loggedIn) {
  router.push({ name: 'locationIndex' })
}

/**
 * Form state and rules
 */

interface FormState {
  identifier: string
  tempPassword: string
  newPassword1: string
  newPassword2: string
}

const formState = reactive<FormState>({
  identifier: '',
  tempPassword: '',
  newPassword1: '',
  newPassword2: '',
})

const sameAsNewPassword1 = (value: any) => {
  // don't bother validating unless both password fields have a value
  if (value === '' || formState.newPassword1 === '') {
    return true
  }  
  return value === formState.newPassword1
}

const formRules = {
  identifier: {
    required,
  },
  tempPassword: {
    required,
  },
  newPassword1: {
    required,
    passwordComplexity: helpers.withMessage(t('validations.fails_password_requirements'), passwordComplexity)
  },
  newPassword2: {
    required,
    sameAsNewPassword1: helpers.withMessage(t('validations.passwords_match'), sameAsNewPassword1),
  },
}

const v$ = useVuelidate(formRules, formState)

/**
 * Submit
 */

const submitting = ref<boolean>(false)

async function submit() {

  const isValid = await v$.value.$validate()

  if (!isValid) {
    return
  }

  submitting.value = true

  const payload = {
    username: formState.identifier,
    tempPassword: formState.tempPassword,
    newPassword: formState.newPassword1,
  }

  store.dispatch('auth/confirmAccount', payload)
    .then(() => {
      NotificationService.success('Account activated! Please log in') // TODO: i18n
      router.push({name: 'login'})
    })
    .catch((error: any) => {
      console.error(error)
      NotificationService.error(error)
    })
    .finally(() => {
      submitting.value = false
    })

}

</script>
